import { useEffect, useRef } from 'react';

/**
 * Browsers redirect a vertical mouse-wheel gesture into horizontal scroll on any
 * element that has overflow-x but no overflow-y (e.g. a tab bar). That makes the
 * whole tab row appear to "scroll sideways" when the user is just trying to
 * scroll the page. This re-forwards predominantly-vertical wheel input to the
 * window instead of letting the element consume it.
 */
export function useNoWheelHijack<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        window.scrollBy(0, e.deltaY);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return ref;
}
